import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { toast } from 'react-toastify';
import ConfirmDialog from '../common/ConfirmDialog';
import Select2Dropdown from '../common/Select2Dropdown';
import JobForm from '../documentation/JobForm';
import { getAuthHeaders } from '../../auth';
import { usePermissions } from '../../services/PermissionsContext';
import '../documentation/JobList.css';   // toolbar, jobs-table, icon-btn, modal-overlay
import '../documentation/JobForm.css';   // section/row/label/input/JobForm look

const API_URL = '/api/admin/tmda-applications/';
const MASTERS_API = '/api/masters/';
const MODULE = 'TMDA APPLICATIONS';

// Every status with its badge colour
const STATUS_META = {
    PENDING: { label: 'Pending', color: '#007bff' },
    SUBMITTED: { label: 'Submitted', color: '#fd7e14' },
    RESUBMITTED: { label: 'Re-submitted', color: '#fd7e14' },
    VERIFICATION_APPROVAL: { label: 'Verification Approval', color: '#17a2b8' },
    WAITING_FOR_PAYMENT: { label: 'Waiting For Payment', color: '#6f42c1' },
    RESUBMIT: { label: 'Re-submit', color: '#ffc107' },
    RETURN: { label: 'Return', color: '#ffc107' },
    APPROVED: { label: 'Approved', color: '#28a745' },
    REJECTED: { label: 'Rejected', color: '#dc3545' },
};

// Tabs group one or more statuses; QUERY flashes when it holds records
const STATUS_TABS = [
    { key: 'PENDING', label: 'Pending', statuses: ['PENDING'], css: 'tmda-pending-tab' },
    { key: 'SUBMITTED', label: 'Submitted', statuses: ['SUBMITTED', 'RESUBMITTED', 'VERIFICATION_APPROVAL'], css: 'tmda-submitted-tab' },
    { key: 'QUERY', label: 'Query', statuses: ['RESUBMIT', 'RETURN'], css: 'tmda-query-tab', flash: true },
    { key: 'WAITING_PAYMENT', label: 'Waiting Payment', statuses: ['WAITING_FOR_PAYMENT'], css: 'tmda-waiting-payment-tab', flash: true },
    { key: 'APPROVED', label: 'Approved', statuses: ['APPROVED'], css: 'tmda-approved-tab' },
    { key: 'REJECTED', label: 'Rejected', statuses: ['REJECTED'], css: 'tmda-rejected-tab' },
];

// Statuses a user may select manually; PENDING/SUBMITTED/RESUBMITTED are derived
const USER_SETTABLE_STATUSES = ['VERIFICATION_APPROVAL', 'WAITING_FOR_PAYMENT', 'RESUBMIT', 'RETURN', 'APPROVED', 'REJECTED'];
const QUERY_STATUSES = ['RESUBMIT', 'RETURN'];
const REMARKS_REQUIRED_STATUSES = ['REJECTED', 'RESUBMIT', 'RETURN', 'RESUBMITTED'];

// Wraps remarks to the next line after ~150 characters (word-boundary aware,
// hard-splits only when a single word exceeds the limit).
const wrapRemarks = (text, limit = 150) => {
    const words = String(text || '').toUpperCase().split(/\s+/).filter(Boolean);
    const lines = [];
    let line = '';
    for (const w of words) {
        line = line && (line + ' ' + w).length <= limit ? `${line} ${w}` : (lines.push(line), w);
        while (line.length > limit) {
            lines.push(line.slice(0, limit));
            line = line.slice(limit);
        }
    }
    if (line) lines.push(line);
    return lines.filter(Boolean).join('\n');
};
const TRACKING_REQUIRED_STATUSES = ['VERIFICATION_APPROVAL', 'WAITING_FOR_PAYMENT', 'APPROVED'];

// Allowed next statuses per current status; absent keys have no transitions
const STATUS_TRANSITIONS = {
    PENDING: ['SUBMITTED'],
    SUBMITTED: ['VERIFICATION_APPROVAL', 'RESUBMIT', 'RETURN'],
    RESUBMITTED: ['VERIFICATION_APPROVAL', 'WAITING_FOR_PAYMENT', 'RESUBMIT', 'RETURN'],
    VERIFICATION_APPROVAL: ['WAITING_FOR_PAYMENT', 'RESUBMIT', 'APPROVED', 'REJECTED'],
    WAITING_FOR_PAYMENT: ['APPROVED', 'REJECTED'],
    RESUBMIT: ['RESUBMITTED'],
    RETURN: ['RESUBMITTED'],
};

const isoLocal = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
const TODAY = isoLocal(new Date());
const MIN_DATE = isoLocal(new Date(Date.now() - 2 * 86400000)); // 2 days back

const emptyForm = () => ({
    application_date: '',
    received_date: TODAY,
    permit_type: 'TMDA',
    customer: '',
    supplier: '',
    proforma_number: '',
    ucr_number: '',
    application_number: '',
    tracking_number: '',
    status: 'PENDING',
    remarks: '',
});

const fmtDate = (v) => {
    if (!v) return '-';
    const s = String(v).slice(0, 10);
    const m = s.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    return m ? `${m[3]}-${m[2]}-${m[1]}` : s;
};
const fmtDateTime = (v) => {
    if (!v) return '-';
    const d = new Date(v);
    return isNaN(d) ? String(v) : `${d.toLocaleDateString('en-GB')} ${d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}`;
};

const HISTORY_LABELS = Object.fromEntries([
    ['CREATED', 'Created'], ['UPDATED', 'Updated'],
    ...Object.entries(STATUS_META).map(([k, v]) => [k, v.label]),
]);

const TMDAApplications = () => {
    const { hasPermission } = usePermissions();
    const canView = hasPermission(MODULE, 'can_view');
    const canCreate = hasPermission(MODULE, 'can_create');
    const canEdit = hasPermission(MODULE, 'can_edit');
    const canDelete = hasPermission(MODULE, 'can_delete');

    const [applications, setApplications] = useState([]);
    const [loading, setLoading] = useState(true);
    const [activeTab, setActiveTab] = useState('PENDING');

    // Filters
    const [filterCustomer, setFilterCustomer] = useState('');
    const [filterText, setFilterText] = useState('');

    // Dropdown data
    const [customers, setCustomers] = useState([]);
    const [suppliers, setSuppliers] = useState([]);

    // Modal
    const [modalMode, setModalMode] = useState(null); // 'create' | 'edit' | 'view'
    const [current, setCurrent] = useState(null);   // row being edited/viewed
    const [form, setForm] = useState(emptyForm());
    const [saving, setSaving] = useState(false);
    const [confirmDialog, setConfirmDialog] = useState({ open: false });
    const [resendingEmailId, setResendingEmailId] = useState(null);

    // Status edit modal (opened from the Status column pen, requires delete permission)
    const [statusModal, setStatusModal] = useState(false);
    const [statusRow, setStatusRow] = useState(null);
    const [statusForm, setStatusForm] = useState({ status: '', tracking_number: '', remarks: '' });
    const [statusSaving, setStatusSaving] = useState(false);

    // Job view modal (double-click from the Approved tab Job Created column)
    const [jobViewModal, setJobViewModal] = useState({ open: false, job: null });
    const openJobView = async (jobId) => {
        try {
            const res = await fetch(`/api/documentation/jobs/${jobId}/`, { headers: getAuthHeaders() });
            if (!res.ok) {
                toast.error('Failed to load job details');
                return;
            }
            setJobViewModal({ open: true, job: await res.json() });
        } catch (e) {
            toast.error('Error loading job');
        }
    };
    const closeJobView = () => setJobViewModal({ open: false, job: null });

    const fetchApplications = useCallback(async () => {
        setLoading(true);
        try {
            const res = await fetch(API_URL, { headers: getAuthHeaders() });
            if (!res.ok) throw new Error('Failed to load applications');
            const data = await res.json();
            setApplications(data.results || data);
        } catch (err) {
            toast.error(err.message);
        } finally {
            setLoading(false);
        }
    }, []);

    const fetchLookups = useCallback(async () => {
        try {
            const [cRes, sRes] = await Promise.all([
                fetch(`${MASTERS_API}customers/`, { headers: getAuthHeaders() }),
                fetch(`${MASTERS_API}suppliers/`, { headers: getAuthHeaders() }),
            ]);
            if (cRes.ok) {
                const data = await cRes.json();
                setCustomers(data.results || data);
            }
            if (sRes.ok) {
                const data = await sRes.json();
                setSuppliers(data.results || data);
            }
        } catch {}
    }, []);

    useEffect(() => {
        if (canView) {
            fetchApplications();
            fetchLookups();
        }
    }, [canView, fetchApplications, fetchLookups]);

    // ── Tabs / filtering ───────────────────────────────────────────────────

    const currentTab = STATUS_TABS.find(t => t.key === activeTab) || STATUS_TABS[0];

    // Tab key that contains a given status (e.g. RESUBMIT -> QUERY, PENDING -> PENDING)
    const tabForStatus = (status) => STATUS_TABS.find(t => t.statuses.includes(status))?.key;

    const filtered = useMemo(() => {
        let rows = applications.filter(a => currentTab.statuses.includes(a.status));
        if (filterCustomer) rows = rows.filter(a => String(a.customer) === String(filterCustomer));
        if (filterText.trim()) {
            const t = filterText.trim().toLowerCase();
            rows = rows.filter(a =>
                (a.ucr_number || '').toLowerCase().includes(t) ||
                (a.application_number || '').toLowerCase().includes(t) ||
                (a.tracking_number || '').toLowerCase().includes(t)
            );
        }
        // Longest status label first, then descending application date (then id)
        return [...rows].sort((a, b) =>
            (STATUS_META[b.status]?.label || '').length - (STATUS_META[a.status]?.label || '').length ||
            String(b.application_date || '').localeCompare(String(a.application_date || '')) || b.id - a.id
        );
    }, [applications, currentTab, filterCustomer, filterText]);

    const counts = useMemo(() => {
        const c = Object.fromEntries(STATUS_TABS.map(t => [t.key, 0]));
        applications.forEach(a => {
            const tab = STATUS_TABS.find(t => t.statuses.includes(a.status));
            if (tab) c[tab.key] += 1;
        });
        return c;
    }, [applications]);

    // ── Modal ──────────────────────────────────────────────────────────────

    const [showHistory, setShowHistory] = useState(false);

    const openModal = (mode, row = null) => {
        if (mode === 'create' && !canCreate) return toast.error("You don't have permission to create applications.");
        if (mode === 'edit' && !canEdit && !canDelete) return toast.error("You don't have permission to edit applications.");
        setCurrent(row);
        setForm(row ? {
            // On the Pending-tab edit, Application Date defaults to today
            application_date: row.application_date || (row.status === 'PENDING' && mode === 'edit' ? TODAY : ''),
            received_date: row.received_date || '',
            permit_type: row.permit_type || 'TMDA',
            customer: row.customer || '',
            supplier: row.supplier || '',
            proforma_number: row.proforma_number || '',
            ucr_number: row.ucr_number || '',
            application_number: row.application_number || '',
            tracking_number: row.tracking_number || '',
            status: row.status || 'PENDING',
            remarks: row.remarks || '',
        } : emptyForm());
        setModalMode(mode);
    };

    const closeModal = () => {
        setModalMode(null);
        setCurrent(null);
        setForm(emptyForm());
    };

    const openStatusEdit = (row) => {
        setStatusRow(row);
        setStatusForm({
            status: row.status === 'PENDING' ? row.status : '',
            application_number: (row.application_number || '').toUpperCase(),
            application_date: row.application_date || TODAY,
            tracking_number: (row.tracking_number || '').toUpperCase(),
            remarks: '',
            send_email: '',
        });
        setStatusModal(true);
    };

    const handleStatusSave = async () => {
        const missing = [];
        if (!statusForm.status) missing.push('Status');
        if (statusForm.status === 'SUBMITTED') {
            if (!(statusForm.application_number || '').trim()) missing.push('Application Number');
            if (!statusForm.application_date) missing.push('Application Date');
        }
        if (TRACKING_REQUIRED_STATUSES.includes(statusForm.status) && !(statusForm.tracking_number || '').trim()) {
            missing.push('Tracking Number');
        }
        if (REMARKS_REQUIRED_STATUSES.includes(statusForm.status) && !(statusForm.remarks || '').trim()) {
            missing.push(`Remarks (required for ${STATUS_META[statusForm.status].label})`);
        }
        if (statusForm.status === 'RESUBMIT' && !statusForm.send_email) {
            missing.push('Send Email (select Yes or No)');
        }
        if (missing.length) {
            toast.warn(`Required fields missing: ${missing.join(', ')}`);
            return;
        }
        setStatusSaving(true);
        try {
            const res = await fetch(`${API_URL}${statusRow.id}/`, {
                method: 'PATCH',
                headers: getAuthHeaders(),
                body: JSON.stringify({
                    status: statusForm.status,
                    ...(statusRow.status === 'PENDING' ? {
                        application_number: statusForm.application_number,
                        application_date: statusForm.application_date,
                    } : {}),
                    tracking_number: statusForm.tracking_number,
                    ...(statusChanged || statusForm.remarks.trim() ? { remarks: statusForm.remarks } : {}),
                    ...(statusForm.status === 'RESUBMIT' ? { send_email: statusForm.send_email === 'YES' } : {}),
                }),
            });
            const data = await res.json().catch(() => ({}));
            if (!res.ok) {
                const msg = Object.values(data).flat().join(' ') || 'Failed to update application.';
                throw new Error(msg);
            }
            toast.success('Application updated.');
            if (data.email) {
                if (data.email.sent) toast.success(data.email.message || 'Email sent to customer.');
                else toast.warn(`Email not sent: ${data.email.error}`);
            }
            setStatusModal(false);
            setStatusRow(null);
            const tabKey = tabForStatus(data.status || statusForm.status);
            if (tabKey) setActiveTab(tabKey);
            fetchApplications();
        } catch (err) {
            toast.error(err.message);
        } finally {
            setStatusSaving(false);
        }
    };

    const statusEditOptions = statusRow
        ? (STATUS_TRANSITIONS[statusRow.status] || [])
            .map(k => ({ id: k, text: STATUS_META[k].label }))
        : [];
    const statusChanged = !!statusRow && statusForm.status !== statusRow.status;

    const UPPERCASE_FIELDS = new Set(['proforma_number', 'ucr_number', 'application_number', 'tracking_number', 'remarks']);
    const setField = (k, v) => setForm(f => ({ ...f, [k]: UPPERCASE_FIELDS.has(k) && typeof v === 'string' ? v.toUpperCase() : v }));

    // Application number drives the derived statuses: PENDING when empty,
    // SUBMITTED (or RESUBMITTED for queried records) once a number is entered.
    const handleAppNoChange = (raw) => {
        const v = raw.toUpperCase();
        setForm(f => {
            const has = !!v.trim();
            const status = has
                ? (QUERY_STATUSES.includes(f.status) ? 'RESUBMITTED' : f.status === 'PENDING' ? 'SUBMITTED' : f.status)
                : (['SUBMITTED', 'RESUBMITTED'].includes(f.status) ? 'PENDING' : f.status);
            return { ...f, application_number: v, status };
        });
    };

    const handleSave = async () => {
        const missing = [];
        if (!form.received_date) missing.push('Received Date');
        if (!form.customer) missing.push('Customer');
        if (!form.supplier) missing.push('Supplier');
        if (!(form.proforma_number || '').trim()) missing.push('Proforma Number');
        if (!(form.ucr_number || '').trim()) missing.push('UCR Number');
        if ((form.application_number || '').trim() && !form.application_date) {
            missing.push('Application Date (required with Application Number)');
        }
        if (TRACKING_REQUIRED_STATUSES.includes(form.status) && !(form.tracking_number || '').trim()) {
            missing.push('Tracking Number (required from Verification Approval)');
        }
        if (REMARKS_REQUIRED_STATUSES.includes(form.status) && !(form.remarks || '').trim()) {
            missing.push(`Remarks (required for ${STATUS_META[form.status].label})`);
        }
        if (missing.length) {
            toast.warn(`Required fields missing: ${missing.join(', ')}`);
            return;
        }
        const isEdit = modalMode === 'edit';
        setSaving(true);
        try {
            const res = await fetch(isEdit ? `${API_URL}${current.id}/` : API_URL, {
                method: isEdit ? 'PUT' : 'POST',
                headers: getAuthHeaders(),
                body: JSON.stringify({
                    ...form,
                    permit_type: 'TMDA',
                    // Omit when blank — model default applies on create; on update the field is left unchanged
                    application_date: form.application_date || undefined,
                    received_date: form.received_date || null,
                    customer: form.customer || null,
                    supplier: form.supplier || null,
                }),
            });
            const data = await res.json().catch(() => ({}));
            if (!res.ok) {
                const msg = Object.values(data).flat().join(' ') || 'Failed to save application.';
                throw new Error(msg);
            }
            toast.success(`Application ${isEdit ? 'updated' : 'created'} successfully!`);
            if (data.email) {
                if (data.email.sent) toast.success(data.email.message || 'Email sent to customer.');
                else toast.warn(`Email not sent: ${data.email.error}`);
            }
            closeModal();
            const tabKey = tabForStatus(data.status || form.status);
            if (tabKey) setActiveTab(tabKey);
            fetchApplications();
        } catch (err) {
            toast.error(err.message);
        } finally {
            setSaving(false);
        }
    };

    const resendStatusEmail = async (row) => {
        setResendingEmailId(row.id);
        try {
            const res = await fetch(`${API_URL}${row.id}/resend-status-email/`, {
                method: 'POST',
                headers: getAuthHeaders(),
            });
            const data = await res.json().catch(() => ({}));
            if (!res.ok) {
                throw new Error(data.error || data.detail || 'Failed to resend email.');
            }
            if (data.email && data.email.sent) toast.success(data.email.message || 'Email sent to customer.');
            else toast.warn(`Email not sent: ${(data.email && data.email.error) || 'Unknown error'}`);
            fetchApplications();
        } catch (err) {
            toast.error(err.message);
        } finally {
            setResendingEmailId(null);
        }
    };

    const handleDelete = (row) => {
        if (!canDelete) return toast.error("You don't have permission to delete applications.");
        setConfirmDialog({
            open: true,
            title: 'Confirm Deletion',
            message: `Delete application${row.application_number ? ` "${row.application_number}"` : ''} for ${row.customer_name || 'this customer'}? This cannot be undone.`,
            onConfirm: async () => {
                try {
                    const res = await fetch(`${API_URL}${row.id}/`, { method: 'DELETE', headers: getAuthHeaders() });
                    if (!res.ok) throw new Error('Failed to delete application');
                    toast.success('Application deleted.');
                    setConfirmDialog({ open: false });
                    fetchApplications();
                } catch (err) {
                    toast.error(err.message);
                }
            },
            onCancel: () => setConfirmDialog({ open: false }),
        });
    };

    // ── Render helpers ─────────────────────────────────────────────────────

    const readOnly = modalMode === 'view';
    // Edit opened from the Pending tab: only Application Date + Application Number editable
    const fieldReadOnly = readOnly;
    const customerOptions = customers.map(c => ({ id: c.id, text: c.name }));
    const supplierOptions = suppliers.map(s => ({ id: s.id, text: s.name }));

    const showActionCol = canDelete || canEdit;
    const colCount = 9 + (showActionCol ? 1 : 0) + (activeTab === 'APPROVED' ? 1 : 0);

    const statusBadge = (s) => {
        const meta = STATUS_META[s];
        return (
            <span style={{
                display: 'inline-block', padding: '3px 10px', borderRadius: 12,
                fontSize: 11, fontWeight: 600,
                background: meta ? meta.color : '#6c757d',
                color: QUERY_STATUSES.includes(s) ? '#212529' : '#fff',
            }}>{meta ? meta.label : (s || '-')}</span>
        );
    };

    if (!canView) {
        return (
            <div className="job-list-container">
                <h3>Permission Denied</h3>
                <p>You do not have permission to view this page.</p>
            </div>
        );
    }

    return (
        <div className="job-list-container">
            <h2>TMDA Applications</h2>

            {/* Toolbar: filters + new */}
            <div className="toolbar">
                <div style={{ width: 220 }}>
                    <Select2Dropdown
                        options={customerOptions}
                        value={filterCustomer}
                        onChange={setFilterCustomer}
                        placeholder="Filter by Customer"
                        valueKey="id"
                        displayKey="text"
                        clearable
                    />
                </div>
                <input
                    type="text"
                    className="search-input"
                    style={{ flex: '0 0 260px', minWidth: 0 }}
                    placeholder="Filter by UCR / Application No / Tracking No"
                    value={filterText}
                    onChange={e => setFilterText(e.target.value)}
                />
                <div style={{ flex: 1 }} />
                {canCreate && (
                    <button className="add-btn" onClick={() => openModal('create')}>
                        + New Application
                    </button>
                )}
            </div>

            {/* Status tabs */}
            <div className="custom-tabs-container">
                {STATUS_TABS.map(t => (
                    <div
                        key={t.key}
                        className={`custom-tab ${t.css} ${activeTab === t.key ? 'active' : ''} ${t.flash && counts[t.key] > 0 ? 'stage-3-flashing' : ''}`}
                        onClick={() => setActiveTab(t.key)}
                    >
                        {t.label} ({counts[t.key]})
                    </div>
                ))}
            </div>

            {/* Table */}
            <div style={{ overflowX: 'auto' }}>
                <table className="jobs-table tmda-table">
                    <thead>
                        <tr>
                            <th>Received Date</th>
                            <th>Application Date</th>
                            <th>Customer</th>
                            <th>Supplier</th>
                            <th>Proforma No.</th>
                            <th>UCR No.</th>
                            <th>Application No.</th>
                            <th>Tracking No.</th>
                            <th>Status</th>
                            {activeTab === 'APPROVED' && <th>Job Created</th>}
                            {showActionCol && <th>Action</th>}
                        </tr>
                    </thead>
                    <tbody>
                        {loading ? (
                            <tr><td colSpan={colCount} style={{ textAlign: 'center' }}>Loading...</td></tr>
                        ) : filtered.length === 0 ? (
                            <tr><td colSpan={colCount} style={{ textAlign: 'center', color: '#94a3b8' }}>No {currentTab.label.toLowerCase()} applications found.</td></tr>
                        ) : filtered.map((row, idx) => (
                            <React.Fragment key={row.id}>
                            <tr className={idx % 2 === 1 ? 'tmda-alt-row' : ''}>
                                <td>{fmtDate(row.received_date)}</td>
                                <td>{fmtDate(row.application_date)}</td>
                                <td><strong>{row.customer_short_name || row.customer_name || '-'}</strong></td>
                                <td>{row.supplier_name || '-'}</td>
                                <td>{(row.proforma_number || '-').toUpperCase()}</td>
                                <td>{(row.ucr_number || '-').toUpperCase()}</td>
                                <td>{(row.application_number || '-').toUpperCase()}</td>
                                <td>{(row.tracking_number || '-').toUpperCase()}</td>
                                <td>
                                    {statusBadge(row.status)}
                                    {canEdit && (STATUS_TRANSITIONS[row.status] || []).length > 0 && (
                                        <button className="icon-btn" title="Edit Status" style={{ marginLeft: 6 }} onClick={() => openStatusEdit(row)}>
                                            <i className="fa fa-pencil-alt"></i>
                                        </button>
                                    )}
                                </td>
                                {activeTab === 'APPROVED' && (
                                    <td
                                        style={{ textAlign: 'center', cursor: row.job_created ? 'pointer' : 'default' }}
                                        title={row.job_created ? `JOB ID: ${row.job_id} | IDF NO: ${row.job_idf_no || '-'} | Double-click to view job` : 'No linked job'}
                                        onDoubleClick={() => row.job_created && openJobView(row.job_id)}
                                    >
                                        {row.job_created
                                            ? <i className="fa fa-check" style={{ color: '#28a745', fontSize: 16 }}></i>
                                            : <i className="fa fa-hourglass-half" style={{ color: '#fd7e14', fontSize: 16 }}></i>}
                                    </td>
                                )}
                                {showActionCol && (
                                <td className="actions">
                                    {canEdit && row.status_email_failed && (
                                        <button
                                            className="icon-btn"
                                            title={`Email not sent${row.status_email_error ? `: ${row.status_email_error}` : ''}. Click to resend.`}
                                            style={{ color: '#dc3545' }}
                                            disabled={resendingEmailId === row.id}
                                            onClick={() => resendStatusEmail(row)}
                                        >
                                            <i className={`fa ${resendingEmailId === row.id ? 'fa-spinner fa-spin' : 'fa-envelope'}`}></i>
                                        </button>
                                    )}
                                    {canDelete && (
                                        <>
                                            <button className="icon-btn view-btn" title="View" onClick={() => openModal('view', row)}>
                                                <i className="fa fa-eye"></i>
                                            </button>
                                            <button className="icon-btn delete-btn" title="Delete" onClick={() => handleDelete(row)}>
                                                <i className="fa fa-trash"></i>
                                            </button>
                                        </>
                                    )}
                                </td>
                                )}
                            </tr>
                            {row.remarks && (
                                <tr className="tmda-remarks-row">
                                    <td colSpan={colCount} style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}><strong style={{ color: '#000' }}>REMARKS</strong> | <span style={{ color: '#dc3545' }}>{wrapRemarks(row.remarks)}</span></td>
                                </tr>
                            )}
                            </React.Fragment>
                        ))}
                    </tbody>
                </table>
            </div>

            {/* Create / Edit / View modal — JobForm style */}
            {modalMode && (
                <div className="modal-overlay" onClick={closeModal}>
                    <div className="dialog-container" style={{ width: '92vw', maxWidth: 820 }} onClick={e => e.stopPropagation()}>
                        <div className="dialog-header">
                            <h3>{modalMode === 'create' ? 'New TMDA Application' : modalMode === 'edit' ? 'Edit TMDA Application' : 'View TMDA Application'}</h3>
                            <button className="icon-btn" onClick={closeModal} title="Close">
                                <i className="fa fa-times" />
                            </button>
                        </div>
                        <div className="dialog-body">
                            {modalMode !== 'create' && current && (
                                <div className="section">
                                    <h2
                                        style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}
                                        onClick={() => setShowHistory(v => !v)}
                                        title={showHistory ? 'Collapse Approval History' : 'Expand Approval History'}
                                    >
                                        <i className={`fa ${showHistory ? 'fa-chevron-down' : 'fa-chevron-right'}`} style={{ fontSize: 11 }} />
                                        HISTORY
                                    </h2>
                                    {showHistory && (
                                    <div className="section-content" style={{ padding: 0 }}>
                                        <table className="jobs-table" style={{ margin: 0 }}>
                                            <thead>
                                                <tr>
                                                    <th>Date/Time</th>
                                                    <th>Action</th>
                                                    <th>By</th>
                                                    <th>Remarks</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {(current.history || []).length === 0 ? (
                                                    <tr>
                                                        <td>{fmtDateTime(current.created_at)}</td>
                                                        <td>Created</td>
                                                        <td>{current.created_by_name || '-'}</td>
                                                        <td>-</td>
                                                    </tr>
                                                ) : current.history.map(h => (
                                                    <tr key={h.id}>
                                                        <td>{fmtDateTime(h.acted_at)}</td>
                                                        <td>{HISTORY_LABELS[h.action] || h.action}</td>
                                                        <td>{h.actor || '-'}</td>
                                                        <td style={{ maxWidth: 240, whiteSpace: 'pre-wrap', color: '#dc3545' }}>{(h.remarks || '-').toUpperCase()}</td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                    )}
                                </div>
                            )}

                            <div className="section">
                                <h2>APPLICATION DETAILS</h2>
                                <div className="section-content">
                                    <div className="row" style={{ gridTemplateColumns: '1fr 1fr 1fr 1fr' }}>
                                        <div>
                                            <label className="required">Received Date</label>
                                            <input
                                                type="date"
                                                value={form.received_date}
                                                onChange={e => setField('received_date', e.target.value)}
                                                min={MIN_DATE}
                                                max={TODAY}
                                                readOnly={fieldReadOnly}
                                            />
                                        </div>
                                        <div>
                                            <label className="required">Customer</label>
                                            <Select2Dropdown
                                                options={customerOptions}
                                                value={form.customer}
                                                onChange={v => setField('customer', v)}
                                                placeholder="SELECT CUSTOMER"
                                                valueKey="id"
                                                displayKey="text"
                                                clearable
                                                disabled={fieldReadOnly}
                                            />
                                        </div>
                                        <div>
                                            <label className="required">Supplier</label>
                                            <Select2Dropdown
                                                options={supplierOptions}
                                                value={form.supplier}
                                                onChange={v => setField('supplier', v)}
                                                placeholder="SELECT SUPPLIER"
                                                valueKey="id"
                                                displayKey="text"
                                                clearable
                                                disabled={fieldReadOnly}
                                            />
                                        </div>
                                        <div>
                                            <label className="required">Proforma Number</label>
                                            <input
                                                type="text"
                                                value={form.proforma_number}
                                                onChange={e => setField('proforma_number', e.target.value)}
                                                readOnly={fieldReadOnly}
                                            />
                                        </div>
                                    </div>

                                    <div className="row" style={{ gridTemplateColumns: '1fr' }}>
                                        <div>
                                            <label className="required">UCR Number</label>
                                            <input
                                                type="text"
                                                maxLength={30}
                                                value={form.ucr_number}
                                                onChange={e => setField('ucr_number', e.target.value)}
                                                readOnly={fieldReadOnly}
                                            />
                                        </div>
                                    </div>

                                    {modalMode !== 'create' && (
                                        <div className="row" style={{ gridTemplateColumns: '1fr 1fr 1fr' }}>
                                            <div>
                                                <label className={(form.application_number || '').trim() ? 'required' : ''}>Application Date</label>
                                                <input
                                                    type="date"
                                                    value={form.application_date}
                                                    onChange={e => setField('application_date', e.target.value)}
                                                    min={MIN_DATE}
                                                    max={TODAY}
                                                    readOnly={readOnly}
                                                />
                                            </div>
                                            <div>
                                                <label>Application Number</label>
                                                <input
                                                    type="text"
                                                    value={form.application_number}
                                                    onChange={e => handleAppNoChange(e.target.value)}
                                                    readOnly={readOnly}
                                                />
                                            </div>
                                            <div>
                                                <label className={TRACKING_REQUIRED_STATUSES.includes(form.status) ? 'required' : ''}>Tracking Number</label>
                                                <input
                                                    type="text"
                                                    value={form.tracking_number}
                                                    onChange={e => setField('tracking_number', e.target.value)}
                                                    readOnly={fieldReadOnly}
                                                />
                                            </div>
                                        </div>
                                    )}

                                    {modalMode !== 'create' && (
                                        <div className="row" style={{ gridTemplateColumns: '1fr' }}>
                                            <div>
                                                <label>Status</label>
                                                <Select2Dropdown
                                                    options={(() => {
                                                        const opts = USER_SETTABLE_STATUSES.map(k => ({ id: k, text: STATUS_META[k].label }));
                                                        const cur = STATUS_META[form.status];
                                                        return (cur && !USER_SETTABLE_STATUSES.includes(form.status) ? [{ id: form.status, text: cur.label }, ...opts] : opts);
                                                    })()}
                                                    value={form.status}
                                                    onChange={v => setField('status', v)}
                                                    placeholder="SELECT STATUS"
                                                    valueKey="id"
                                                    displayKey="text"
                                                    searchable={false}
                                                    disabled={fieldReadOnly}
                                                />
                                            </div>
                                        </div>
                                    )}

                                    {modalMode !== 'create' && (
                                        <div className="row" style={{ gridTemplateColumns: '1fr' }}>
                                            <div>
                                                <label className={REMARKS_REQUIRED_STATUSES.includes(form.status) ? 'required' : ''}>
                                                    Remarks {REMARKS_REQUIRED_STATUSES.includes(form.status) && <span style={{ color: '#dc3545' }}>(required for {STATUS_META[form.status].label})</span>}
                                                </label>
                                                <textarea
                                                    rows={2}
                                                    value={form.remarks}
                                                    onChange={e => setField('remarks', e.target.value)}
                                                    readOnly={fieldReadOnly}
                                                />
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>

                            <div className="buttons">
                                {modalMode === 'view' ? (
                                    <>
                                        <button className="cancel-btn" onClick={closeModal}>Close</button>
                                        {canDelete && (
                                            <button onClick={() => openModal('edit', current)}>Edit</button>
                                        )}
                                    </>
                                ) : (
                                    <>
                                        <button className="cancel-btn" onClick={closeModal} disabled={saving}>Cancel</button>
                                        <button onClick={handleSave} disabled={saving}>
                                            {saving ? 'Saving...' : modalMode === 'edit' ? 'Update' : 'Save'}
                                        </button>
                                    </>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Status / Tracking / Remarks edit modal (opened from the Status column) */}
            {statusModal && statusRow && (
                <div className="modal-overlay" onClick={() => setStatusModal(false)}>
                    <div className="dialog-container" style={{ width: '92vw', maxWidth: 460 }} onClick={e => e.stopPropagation()}>
                        <div className="dialog-header">
                            <h3>Edit TMDA Application</h3>
                            <button className="icon-btn" onClick={() => setStatusModal(false)} title="Close">
                                <i className="fa fa-times" />
                            </button>
                        </div>
                        <div className="dialog-body">
                            <div className="section">
                                <h2>UPDATE APPLICATION</h2>
                                <div className="section-content">
                                    {statusRow.status === 'PENDING' ? (
                                        <div className="row" style={{ gridTemplateColumns: '1fr 1fr 1fr' }}>
                                            <div>
                                                <label className="required">Application Date</label>
                                                <input
                                                    type="date"
                                                    value={statusForm.application_date}
                                                    onChange={e => setStatusForm(f => ({ ...f, application_date: e.target.value }))}
                                                    min={MIN_DATE}
                                                    max={TODAY}
                                                />
                                            </div>
                                            <div>
                                                <label className="required">Application Number</label>
                                                <input
                                                    type="text"
                                                    value={statusForm.application_number}
                                                    onChange={e => setStatusForm(f => ({
                                                        ...f,
                                                        application_number: e.target.value.toUpperCase(),
                                                        status: e.target.value.trim() ? 'SUBMITTED' : 'PENDING',
                                                    }))}
                                                />
                                            </div>
                                            <div>
                                                <label>Status</label>
                                                <input type="text" value={STATUS_META[statusForm.status]?.label || ''} readOnly />
                                            </div>
                                        </div>
                                    ) : (
                                        <>
                                            <div className="row" style={{ gridTemplateColumns: statusForm.status === 'RESUBMIT' ? '1fr 1fr' : '1fr' }}>
                                                <div>
                                                    <label className="required">Status</label>
                                                    <Select2Dropdown
                                                        options={statusEditOptions}
                                                        value={statusForm.status}
                                                        onChange={v => setStatusForm(f => ({ ...f, status: v }))}
                                                        placeholder="SELECT STATUS"
                                                        valueKey="id"
                                                        displayKey="text"
                                                        searchable={false}
                                                    />
                                                </div>
                                                {statusForm.status === 'RESUBMIT' && (
                                                    <div>
                                                        <label className="required">SEND EMAIL:</label>
                                                        <div style={{ display: 'flex', gap: 16, alignItems: 'center', marginTop: 8 }}>
                                                            <label style={{ display: 'flex', alignItems: 'center', gap: 5, fontWeight: 'normal' }}>
                                                                <input
                                                                    type="radio"
                                                                    name="tmda_send_email"
                                                                    checked={statusForm.send_email === 'YES'}
                                                                    onChange={() => setStatusForm(f => ({ ...f, send_email: 'YES' }))}
                                                                />
                                                                Yes
                                                            </label>
                                                            <label style={{ display: 'flex', alignItems: 'center', gap: 5, fontWeight: 'normal' }}>
                                                                <input
                                                                    type="radio"
                                                                    name="tmda_send_email"
                                                                    checked={statusForm.send_email === 'NO'}
                                                                    onChange={() => setStatusForm(f => ({ ...f, send_email: 'NO' }))}
                                                                />
                                                                No
                                                            </label>
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                            {(statusRow.tracking_number || TRACKING_REQUIRED_STATUSES.includes(statusForm.status)) && (
                                            <div className="row" style={{ gridTemplateColumns: '1fr' }}>
                                                <div>
                                                    <label className={TRACKING_REQUIRED_STATUSES.includes(statusForm.status) ? 'required' : ''}>Tracking Number</label>
                                                    <input
                                                        type="text"
                                                        value={statusForm.tracking_number}
                                                        readOnly={statusRow.status === 'WAITING_FOR_PAYMENT'}
                                                        onChange={e => setStatusForm(f => ({ ...f, tracking_number: e.target.value.toUpperCase() }))}
                                                    />
                                                </div>
                                            </div>
                                            )}
                                            <div className="row" style={{ gridTemplateColumns: '1fr' }}>
                                                <div>
                                                    <label className={REMARKS_REQUIRED_STATUSES.includes(statusForm.status) ? 'required' : ''}>
                                                        Remarks {REMARKS_REQUIRED_STATUSES.includes(statusForm.status) && <span style={{ color: '#dc3545' }}>(required for {STATUS_META[statusForm.status].label})</span>}
                                                    </label>
                                                    <textarea
                                                        rows={3}
                                                        value={statusForm.remarks}
                                                        onChange={e => setStatusForm(f => ({ ...f, remarks: e.target.value.toUpperCase() }))}
                                                    />
                                                </div>
                                            </div>
                                        </>
                                    )}
                                    <div className="buttons">
                                        <button className="cancel-btn" onClick={() => setStatusModal(false)} disabled={statusSaving}>Cancel</button>
                                        <button onClick={handleStatusSave} disabled={statusSaving}>
                                            {statusSaving ? 'Saving...' : 'Update'}
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Linked job — read-only view (double-click from Job Created column) */}
            {jobViewModal.open && (
                <div className="modal-overlay" onClick={closeJobView}>
                    <div className="dialog-container" style={{ width: '95vw', maxWidth: 1400, height: '92vh', display: 'flex', flexDirection: 'column' }} onClick={e => e.stopPropagation()}>
                        <div className="dialog-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <h3>View Job {jobViewModal.job?.kk_file_no || `#${jobViewModal.job?.id}`}</h3>
                            <button className="icon-btn" onClick={closeJobView} title="Close">
                                <i className="fa fa-times" />
                            </button>
                        </div>
                        <div className="dialog-body" style={{ padding: 0, overflowY: 'auto', flex: 1 }}>
                            <JobForm
                                mode="view"
                                initialData={jobViewModal.job}
                                onClose={closeJobView}
                            />
                        </div>
                    </div>
                </div>
            )}

            <ConfirmDialog
                open={confirmDialog.open}
                title={confirmDialog.title}
                message={confirmDialog.message}
                onConfirm={confirmDialog.onConfirm}
                onCancel={confirmDialog.onCancel}
            />
        </div>
    );
};

export default TMDAApplications;
